/* eslint-disable sonarjs/cognitive-complexity */
import { ReactNode, useContext } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { BsPencilSquare } from 'react-icons/bs';
import { LinkContainer } from 'react-router-bootstrap';
import { StatusCodes } from '../../../../v2/js/constants/workflows';
import AvailabilityUrlCopy from '../../../../v2/js/pages/Provider/ProviderHome/AvailabilityUrlCopy';
import {
  CurrentProviderDetail,
  ProviderDetail,
  ProviderLimitedDetail,
  SourceTypeEnum,
} from '../../../api';
import { SessionContext } from '../../contexts/SessionContext';
import { Avatar } from '../Avatar';
import { Card } from '../Card';
import { ClampLines } from '../ClampLines';
import { ClearanceBadge } from '../ClearanceBadge';
import { CopyButton } from '../CopyButton';
import {
  PendingConnectionRequestsBadge,
  PendingPlacementRequestsBadge,
} from '../NotificationBadge';
import { Skeleton } from '../Skeleton';
import './provider-profile-header.scss';
import { RateDisplay } from './components';

interface Props {
  provider?: ProviderLimitedDetail | ProviderDetail | CurrentProviderDetail;
  isProviderLoading: boolean;
  hasAcceptedConnection: boolean;
  isCurrentProvider?: boolean;
  callToAction?: ReactNode;
  isProfileComplete?: boolean;
}

export const ProviderProfileHeader = ({
  provider,
  isProviderLoading,
  hasAcceptedConnection,
  isCurrentProvider = false,
  callToAction,
  isProfileComplete,
}: Props) => {
  const dayRate = isCurrentProvider ? provider?.shift_rate_day : provider?.hourly_day_rate;
  const nightRate = isCurrentProvider ? provider?.shift_rate_night : provider?.hourly_night_rate;
  const _24HRate = isCurrentProvider ? provider?.shift_rate_24h : provider?.hourly_24h_rate;
  const callRate = isCurrentProvider ? provider?.shift_rate_call : provider?.hourly_call_rate;
  const { notifications } = useContext(SessionContext);
  const hasConnectionRequests: boolean = !!notifications?.notifications?.connection_requests;
  const hasAssignmentRequests: boolean = !!notifications?.notifications?.placement_requests;
  const showAvailabilityUrl =
    !!provider?.availability_url && provider?.display_name !== provider?.public_name;
  const isSourcedProvider: boolean = provider?.source_type === SourceTypeEnum.ORGANIZATION;
  const rates = {
    dayRate,
    nightRate,
    _24HRate,
    callRate,
  };

  return (
    <Card className="px-4 pb-4 my-4">
      <Row>
        <Col sm={12} md={9}>
          <div className="mt-2 d-flex flex-wrap flex-column">
            <Avatar
              className="me-4"
              size={120}
              image={provider?.profile_picture}
              enableIconStatus={isCurrentProvider && !isSourcedProvider}
              isProfileComplete={isProfileComplete}
            />
            <div className="flex-grow-1">
              {!isProviderLoading ? (
                <h1 className="flex-grow-1">{provider?.display_name}</h1>
              ) : (
                <Skeleton width={300} height={40} />
              )}
              {!isProviderLoading ? (
                <h5 className="color-primary">
                  <div className="profile-specialty-header">
                    {provider?.specialties?.join(', ')}{' '}
                    {isCurrentProvider && (
                      <LinkContainer to="/app/provider/edit/professional-traits">
                        <Button size="sm" variant="link">
                          <BsPencilSquare className="mb-0" />
                        </Button>
                      </LinkContainer>
                    )}
                  </div>
                  {provider?.clearance_status && (
                    <ClearanceBadge
                      size="xl"
                      status={provider?.clearance_status as StatusCodes}
                      className="mt-2"
                    />
                  )}
                </h5>
              ) : (
                <Skeleton width={150} height={20} />
              )}
            </div>
          </div>
        </Col>
        {isCurrentProvider && (hasConnectionRequests || hasAssignmentRequests) && (
          <div className="mobileOnlyCallToAction">
            {hasConnectionRequests && (
              <LinkContainer to={'/app/provider/connection-requests'}>
                <Button variant="outline-danger" className="flex-grow-1 btn shadow">
                  Pending Connections <PendingConnectionRequestsBadge className="bg-danger" />
                </Button>
              </LinkContainer>
            )}
            {hasAssignmentRequests && (
              <LinkContainer to={'/app/provider/booking-requests'}>
                <Button variant="outline-danger" className="flex-grow-1 btn shadow">
                  Pending Assignments <PendingPlacementRequestsBadge className="bg-danger" />
                </Button>
              </LinkContainer>
            )}
          </div>
        )}
        <Col>
          <div className="callToAction">{callToAction}</div>
        </Col>
      </Row>
      <Card.Divider cardPadding="" space="0" />
      <Row className="h-100 pb-3 g-2">
        <Col
          xs={12}
          md={hasAcceptedConnection || isSourcedProvider ? 12 : 6}
          lg={hasAcceptedConnection || isSourcedProvider ? 12 : 6}
          xl={hasAcceptedConnection || isSourcedProvider ? 7 : 4}
        >
          <Card.SubCard className="p-3 mt-3 h-100 d-flex flex-column">
            <Row>
              <div className="d-flex flex-wrap flex-row align-items-start justify-content-between">
                <Col>
                  <Card.Info isLoading={isProviderLoading} label="Board Certifications">
                    {provider?.board_certification_types?.join(', ')}
                  </Card.Info>
                </Col>
                <Col>
                  <Card.Info isLoading={isProviderLoading} label="Life Support Certifications">
                    {provider?.certification_types?.join(', ')}
                  </Card.Info>
                </Col>
                {isCurrentProvider && (
                  <LinkContainer to="/app/provider/edit/licenses">
                    <Button size="sm" variant="link">
                      <BsPencilSquare className="mb-1" />
                    </Button>
                  </LinkContainer>
                )}
              </div>
            </Row>
            <Row>
              <Col xs={12} lg={9}>
                {isCurrentProvider && provider?.public_name && (
                  <div className="mt-3">
                    <div className="small text-neutral-600">Marketplace Name</div>
                    <div>
                      {provider?.public_name}
                      <CopyButton
                        textToCopy={provider?.public_name}
                        tooltipText="Copy Marketplace Name"
                      />
                    </div>
                  </div>
                )}
              </Col>
            </Row>
            <Row>
              <div className="d-flex flex-wrap flex-row align-items-start justify-content-start">
                <Col>
                  <Card.Info
                    isLoading={isProviderLoading}
                    skeletonLines={4}
                    label="Professional Highlights"
                    className="mt-2 d-inline-block"
                  >
                    {provider?.professional_highlights ? (
                      <ClampLines
                        id="professional_highlights"
                        text={provider?.professional_highlights}
                        lines={4}
                      />
                    ) : isCurrentProvider ? (
                      <LinkContainer to="/app/provider/edit/professional-traits">
                        <Button className="mt-1" size="sm" variant="outline-primary">
                          Add a bio to make your profile stand out
                        </Button>
                      </LinkContainer>
                    ) : (
                      // todo: might be nice to add a button for hco to request a bio
                      <div>-</div>
                    )}
                  </Card.Info>
                </Col>
                {isCurrentProvider && provider?.professional_highlights && (
                  <LinkContainer to="/app/provider/edit/professional-traits">
                    <Button size="sm" variant="link">
                      <BsPencilSquare />
                    </Button>
                  </LinkContainer>
                )}
              </div>
            </Row>
          </Card.SubCard>
        </Col>
        <Col xs={12} md={6} lg={6} xl={2}>
          <Card.SubCard className="p-3 mt-3 h-100 d-flex flex-column">
            <div className="d-flex justify-content-between align-items-start mb-0">
              <h5 className="mb-0">Licenses</h5>
              {isCurrentProvider && (
                <LinkContainer to="/app/provider/edit/licenses">
                  <Button size="sm" variant="link">
                    <BsPencilSquare className="mb-1" />
                  </Button>
                </LinkContainer>
              )}
            </div>
            <Row>
              <Col>
                <Card.Info isLoading={isProviderLoading} label="Medical States">
                  {provider?.medical_licensed_states.length
                    ? provider?.medical_licensed_states.join(', ')
                    : provider?.has_imlc
                      ? provider?.medical_licensed_states.join(', ')
                      : 'None Listed'}
                  <div className="small text-color-primary">
                    {provider?.has_imlc ? 'Compact License (IMLC)' : ''}{' '}
                  </div>
                </Card.Info>
              </Col>
            </Row>
            <Row>
              <Col>
                <Card.Info isLoading={isProviderLoading} label="DEA States">
                  {provider?.dea_licensed_states.length
                    ? provider?.dea_licensed_states.join(', ')
                    : 'None Listed'}
                </Card.Info>
              </Col>
            </Row>
          </Card.SubCard>
        </Col>
        <Col xs={12} md={6} lg={6} xl={3}>
          <Card.SubCard className="p-3 pb-4 mt-3 h-100">
            <div className="d-flex justify-content-between align-items-start mb-0">
              <h5 className="mb-0">Work Preferences</h5>
              {isCurrentProvider && (
                <LinkContainer to="/app/provider/edit/rates">
                  <Button size="sm" variant="link">
                    <BsPencilSquare className="mb-1" />
                  </Button>
                </LinkContainer>
              )}
            </div>
            <Row className="pt-1">
              <Col xs={6}>
                <Card.Info
                  isLoading={isProviderLoading}
                  label={provider?.work_type ? '' : 'Target Shifts'}
                >
                  {provider?.work_type_display ? (
                    <>
                      <div className="pt-1">{provider?.work_type}</div>
                      <div className="small text-muted">{provider?.work_type_display}</div>
                    </>
                  ) : (
                    <div className="pt-1">Not Listed</div>
                  )}
                </Card.Info>
              </Col>
              <Col xs={6}>
                <Card.Info
                  isLoading={isProviderLoading}
                  label={provider?.travel_type ? '' : 'Travel Area'}
                >
                  {provider?.travel_type ? (
                    <>
                      <div className="pt-1">{provider?.travel_type}</div>
                      <div className="small text-muted">{provider?.travel_type_display}</div>
                    </>
                  ) : (
                    <div className="pt-1">Not Listed</div>
                  )}
                </Card.Info>
              </Col>
              {showAvailabilityUrl && (
                <Col xs={12}>
                  <AvailabilityUrlCopy
                    availabilityUrl={provider?.availability_url}
                    label="Share Scheduling Link"
                    className="tw:justify-start"
                    btnClassName="tw:!p-0"
                  />
                </Col>
              )}
            </Row>
          </Card.SubCard>
        </Col>
        {!hasAcceptedConnection && !isSourcedProvider && (
          <Col xs={12} md={6} lg={6} xl={3}>
            <RateDisplay
              className="p-3 mt-3 h-100"
              isProviderLoading={isProviderLoading}
              isCurrentProvider={isCurrentProvider}
              rates={rates}
              showEditLink={true}
            />
          </Col>
        )}
      </Row>
    </Card>
  );
};
